import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @typedef {object} GetPaymentParams
 * @typedef {object} invoice
 * @property {string} invoice_no
 * @property {string} invoice_id
 * @property {string} pdf_path
 * @property {number} [page]
 * @property {number} [per_page]
 * @property {object} [filters]   // { status: "paid" | "failed" }
 * @property {string} [sort]      // "-created_at"
 */

export const useGetAllPayments = () => {
  const [payments, setPayments] = useState(
    /** @type {PaymentResponseItem[] | null} */ (null)
  );
  const [meta, setMeta] = useState(/** @type {PaginationMeta | null} */ (null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch payments list
   * @async
   * @param {GetPaymentParams} params
   */
  const getPayments = async ({
    page = 1,
    per_page = 10,
    filters = {},
    sort = "",
  } = {}) => {
    try {
      setLoading(true);
      setError("");

      const query = new URLSearchParams();

      query.append("page", String(page));
      query.append("per_page", String(per_page));

      Object.keys(filters).forEach((key) => {
        if (filters[key] !== "" && filters[key] !== undefined) {
          query.append(`filter[${key}]`, filters[key]);
        }
      });

      if (sort) query.append("sort", sort);

      const url = `${TENANT_DOMAIN}/billing/payments?${query.toString()}`;

      const response = await axiosInstance.get(url);

      setPayments(response?.data?.data ?? []);
      setMeta(response?.data?.meta ?? null);
    } catch (err) {
      setPayments(null);
      setMeta(null);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    payments,
    meta,
    loading,
    error,
    getPayments,
  };
};
